/**
 * Clinical case-management hooks (CLN-028/029/030, PAC-022).
 *
 * Wraps the Cases/AI-Gateway clinical surface for a case:
 *   - GET  /api/v1/cases/{id}/clinical          → ClinicalReview (referral package,
 *                                                  decision-support signals, complexity)
 *   - POST /api/v1/cases/{id}/clinical/decision  → ClinicalDecision (go/no-go)
 *
 * The decision-support signals are AI-advisory (HITL) and render via the
 * AIInsightCard. Degrades gracefully on 404 (`available = false`) so the
 * Clinical section shows a friendly disabled state when the sibling endpoint
 * isn't wired yet.
 */
import { useCallback, useEffect, useState } from 'react';
import { apiClient, ApiError } from '../api/client';

/** One question/answer in the provider questionnaire. */
export interface ReferralQA {
  question: string;
  answer: string | null;
}

/** An attachment referenced by the referral package. */
export interface ClinicalAttachment {
  id: string;
  label: string;
  attached: boolean;
}

/** An AI decision-support signal (advisory). */
export interface ClinicalSignal {
  category: string;
  detail: string;
  /** Model confidence 0–1. */
  confidence: number;
  /** Supporting evidence lines. */
  evidence: string[];
}

/** The full clinical review surface for a case. */
export interface ClinicalReview {
  /** Provider questionnaire Q/A pairs. */
  questionnaire: ReferralQA[];
  /** Free-text clinical summary. */
  clinical_summary: string | null;
  attachments: ClinicalAttachment[];
  signals: ClinicalSignal[];
  /** Complexity flag (PAC-022). */
  complexity_flag: boolean;
  complexity_reason?: string | null;
  /** Prior decision, if one has been recorded. */
  decision?: string | null;
  decision_note?: string | null;
  decided_at?: string | null;
}

/** A go/no-go decision submission. */
export interface ClinicalDecisionBody {
  /** `go` | `no_go` | `needs_more_info`. */
  decision: string;
  note?: string;
}

export interface ClinicalDecision {
  decision: string;
  note?: string | null;
  decided_at: string;
}

interface UseClinicalResult {
  review: ClinicalReview | null;
  available: boolean;
  isLoading: boolean;
  isWorking: boolean;
  error: ApiError | null;
  actionError: ApiError | null;
  refresh: () => void;
  submitDecision: (body: ClinicalDecisionBody) => Promise<boolean>;
}

function normalize(data: Partial<ClinicalReview> | null): ClinicalReview {
  return {
    questionnaire: data?.questionnaire ?? [],
    clinical_summary: data?.clinical_summary ?? null,
    attachments: data?.attachments ?? [],
    signals: data?.signals ?? [],
    complexity_flag: !!data?.complexity_flag,
    complexity_reason: data?.complexity_reason ?? null,
    decision: data?.decision ?? null,
    decision_note: data?.decision_note ?? null,
    decided_at: data?.decided_at ?? null,
  };
}

export function useClinical(caseId: string | undefined): UseClinicalResult {
  const [review, setReview] = useState<ClinicalReview | null>(null);
  const [available, setAvailable] = useState(true);
  const [isLoading, setIsLoading] = useState(false);
  const [isWorking, setIsWorking] = useState(false);
  const [error, setError] = useState<ApiError | null>(null);
  const [actionError, setActionError] = useState<ApiError | null>(null);

  const refresh = useCallback(() => {
    if (!caseId) return;
    setIsLoading(true);
    setError(null);
    apiClient
      .get<ClinicalReview>(`/cases/${encodeURIComponent(caseId)}/clinical`)
      .then((data) => {
        setReview(normalize(data));
        setAvailable(true);
        setError(null);
      })
      .catch((err: unknown) => {
        if (err instanceof ApiError && err.status === 404) {
          setAvailable(false);
          setReview(null);
          setError(null);
          return;
        }
        setError(err instanceof ApiError ? err : new ApiError(0, 'ERROR', String(err)));
        setReview(null);
      })
      .finally(() => setIsLoading(false));
  }, [caseId]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const submitDecision = useCallback(
    async (body: ClinicalDecisionBody): Promise<boolean> => {
      if (!caseId) return false;
      setIsWorking(true);
      setActionError(null);
      try {
        const res = await apiClient.post<ClinicalDecision>(
          `/cases/${encodeURIComponent(caseId)}/clinical/decision`,
          body
        );
        setReview((r) =>
          r
            ? {
                ...r,
                decision: res?.decision ?? body.decision,
                decision_note: res?.note ?? body.note ?? r.decision_note,
                decided_at: res?.decided_at ?? new Date().toISOString(),
              }
            : r
        );
        return true;
      } catch (err: unknown) {
        setActionError(err instanceof ApiError ? err : new ApiError(0, 'ERROR', String(err)));
        return false;
      } finally {
        setIsWorking(false);
      }
    },
    [caseId]
  );

  return { review, available, isLoading, isWorking, error, actionError, refresh, submitDecision };
}
